import React, { useState } from 'react';
import {
  AppBar, Toolbar, Typography, Button, Container, Box,
  IconButton, Drawer, List, ListItem, ListItemButton,
  ListItemText, useScrollTrigger, Divider,
} from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import ArrowOutwardIcon from '@mui/icons-material/ArrowOutward';
import { useNavigate } from 'react-router-dom';
import { useStateValue } from '../../../contexto/store';
import { LogoIcon } from '../../tool';
import { useInstitutions } from '../../../hooks/useInstitutions';

const NAV = [
  { label: 'Servicios',   id: 'features' },
  { label: 'Institución', id: 'about' },
  { label: 'Contacto',    id: 'contact' },
];

const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });

const LandingHeader = () => {
  const navigate  = useNavigate();
  const theme     = useTheme();
  const [{ sesionUsuario }] = useStateValue();
  const { institutions } = useInstitutions();
  const inst           = institutions?.[0] ?? null;
  const isAuthenticated = sesionUsuario?.autenticado ?? false;
  const [drawerOpen, setDrawerOpen] = useState(false);
  const scrolled = useScrollTrigger({ disableHysteresis: true, threshold: 80 });

  return (
    <>
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          bgcolor: scrolled ? alpha('#FFFFFF', 0.97) : 'transparent',
          backdropFilter: scrolled ? 'blur(14px)' : 'none',
          borderBottom: `1px solid ${scrolled ? alpha('#000', 0.07) : alpha('#fff', 0.1)}`,
          transition: 'background 0.35s, border-color 0.35s, backdrop-filter 0.35s',
          boxShadow: scrolled ? `0 2px 16px ${alpha('#000', 0.06)}` : 'none',
        }}
      >
        <Container maxWidth="lg">
          <Toolbar disableGutters sx={{ height: 70, justifyContent: 'space-between' }}>

            {/* Brand */}
            <Box
              onClick={() => scrollTo('hero')}
              sx={{ display: 'flex', alignItems: 'center', gap: 1.5, cursor: 'pointer', userSelect: 'none' }}
            >
              {inst?.logo ? (
                <Box component="img" src={inst.logo} alt="Logo" sx={{ width: 36, height: 36, objectFit: 'contain' }} />
              ) : (
                <LogoIcon sx={{ fontSize: 36, color: scrolled ? 'primary.main' : 'rgba(255,255,255,0.92)' }} />
              )}
              <Typography sx={{
                fontWeight: 800,
                fontSize: '0.95rem',
                letterSpacing: 0.3,
                color: scrolled ? 'text.primary' : 'rgba(255,255,255,0.92)',
                display: { xs: 'none', sm: 'block' },
                transition: 'color 0.35s',
              }}>
                {inst?.name ?? 'AGUA POTABLE'}
              </Typography>
            </Box>

            {/* Desktop nav */}
            <Box sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center', gap: 0 }}>
              {NAV.map((item) => (
                <Button
                  key={item.id}
                  onClick={() => scrollTo(item.id)}
                  sx={{
                    color: scrolled ? 'text.secondary' : 'rgba(255,255,255,0.78)',
                    fontWeight: 600,
                    fontSize: '0.86rem',
                    textTransform: 'none',
                    px: 2,
                    borderRadius: '8px',
                    transition: 'all 0.2s',
                    '&:hover': {
                      color: scrolled ? 'primary.main' : '#ffffff',
                      bgcolor: scrolled ? alpha(theme.palette.primary.main, 0.06) : alpha('#fff', 0.1),
                    },
                  }}
                >
                  {item.label}
                </Button>
              ))}
            </Box>

            {/* CTA */}
            <Box sx={{ display: { xs: 'none', md: 'block' } }}>
              <Button
                variant={scrolled ? 'contained' : 'outlined'}
                color="primary"
                endIcon={<ArrowOutwardIcon sx={{ fontSize: '0.9rem !important' }} />}
                onClick={() => navigate(isAuthenticated ? '/dashboard' : '/auth/login')}
                sx={scrolled ? {
                  borderRadius: '8px', fontWeight: 700, fontSize: '0.84rem',
                  textTransform: 'none', px: 2.5, py: 0.9,
                  boxShadow: `0 4px 14px ${alpha(theme.palette.primary.main, 0.22)}`,
                } : {
                  borderColor: alpha('#fff', 0.45),
                  color: '#ffffff',
                  borderRadius: '8px', fontWeight: 700, fontSize: '0.84rem',
                  textTransform: 'none', px: 2.5, py: 0.9,
                  '&:hover': { borderColor: '#fff', bgcolor: alpha('#fff', 0.1) },
                }}
              >
                {isAuthenticated ? 'Mi Panel' : 'Ingresar'}
              </Button>
            </Box>

            {/* Mobile toggle */}
            <IconButton
              onClick={() => setDrawerOpen(true)}
              sx={{ display: { md: 'none' }, color: scrolled ? 'text.primary' : 'rgba(255,255,255,0.9)' }}
              aria-label="Abrir menú"
            >
              <MenuIcon />
            </IconButton>
          </Toolbar>
        </Container>
      </AppBar>

      {/* Mobile Drawer */}
      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        PaperProps={{ sx: { width: 280, bgcolor: theme.palette.primary.darker ?? theme.palette.primary.dark, color: 'white' } }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: 3, pt: 3, pb: 2 }}>
          <Typography sx={{ fontWeight: 800, fontSize: '0.95rem', color: 'rgba(255,255,255,0.9)' }}>
            {inst?.name ?? 'AGUA POTABLE'}
          </Typography>
          <IconButton size="small" onClick={() => setDrawerOpen(false)} sx={{ color: 'rgba(255,255,255,0.6)' }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        <Divider sx={{ borderColor: 'rgba(255,255,255,0.08)', mx: 3 }} />

        <List disablePadding sx={{ mt: 1 }}>
          {[{ label: 'Inicio', id: 'hero' }, ...NAV].map((item) => (
            <ListItem key={item.id} disablePadding>
              <ListItemButton
                onClick={() => { scrollTo(item.id); setDrawerOpen(false); }}
                sx={{ px: 3, py: 1.4, '&:hover': { bgcolor: 'rgba(255,255,255,0.05)' } }}
              >
                <ListItemText primary={item.label} primaryTypographyProps={{ fontWeight: 600, fontSize: '0.95rem', color: 'rgba(255,255,255,0.75)' }} />
              </ListItemButton>
            </ListItem>
          ))}
        </List>

        <Box sx={{ p: 3, mt: 'auto' }}>
          <Button
            fullWidth
            variant="contained"
            color="primary"
            onClick={() => { navigate(isAuthenticated ? '/dashboard' : '/auth/login'); setDrawerOpen(false); }}
            sx={{ fontWeight: 800, textTransform: 'none', borderRadius: '10px', py: 1.4 }}
          >
            {isAuthenticated ? 'Mi Panel' : 'Ingresar al Sistema'}
          </Button>
        </Box>
      </Drawer>
    </>
  );
};

export default LandingHeader;
